import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import FormControl from '@mui/material/FormControl'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import CancelIcon from '@mui/icons-material/Cancel'
import SendIcon from '@mui/icons-material/Send'
import Download from '@mui/icons-material/Download'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { BUTTON_LOADING_ICON, FORM_DIALOG_ACTION_BUTTON_SX, FORM_DIALOG_WARNING_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import AddressSearchModal from '../../component/AddressSearchModal'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DATE_FIELD_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import Snackbars from '../../component/Snackbars'
import StatusChip, { PAYMENT_STATUS_CHIP, SHIPPING_STATUS_CHIP } from '../../component/StatusChip'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseDateParam, parseNumberParam } from '../../utils/listPageState'
import { formatDate } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'
import * as FileSaver from "file-saver"
import * as XLS from "xlsx"
import {
    hasPendingOrderShippingExcelUpload,
    ORDER_SHIPPING_EXCEL_UPLOAD_LEAVE_MESSAGE,
    usePendingOrderShippingExcelUploadUnloadGuard,
} from './orderShippingLeaveGuard'

// 상수 분리
const CONSTANTS = {
    OFFSET: 10,
    MIN_YEAR_OFFSET: 2,
    SHIPPING_STATUS: {
        PREPARING: 1,
        SHIPPING: 2,
        COMPLETED: 3
    },
    PAY_STATUS: {
        CANCELLED: 'cancelled',
        PAID: 'paid'
    }
}

const DEFAULT_SEARCH_SELECT = {
    searchShippingStatus: '',
    searchPayStatus: '',
}

const SHIPPING_EXCEL_FILE_ACCEPT = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
const SHIPPING_EXCEL_FILE_ERROR_MESSAGE = '엑셀 파일만 업로드할 수 있습니다.'
const SHIPPING_NUMBER_ERROR_MESSAGE = '송장번호는 숫자 6~30자리만 입력할 수 있습니다.'
const SHIPPING_NUMBER_PATTERN = /^\d{6,30}$/

const normalizeShippingNumber = (shippingNumber) => {
    const normalized = String(shippingNumber || '').trim().replace(/-/g, '')

    return normalized || ''
}

const isValidShippingNumber = (shippingNumber) => {
    return SHIPPING_NUMBER_PATTERN.test(normalizeShippingNumber(shippingNumber))
}

// 배송 송장 업로드는 기존 운영 기준인 xlsx/xls 엑셀 파일만 통과시킨다.
const isShippingExcelFile = (file) => {
    return Boolean(file) && (
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        || file.type === 'application/vnd.ms-excel'
        || /\.(xlsx|xls)$/i.test(file.name || '')
    )
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 'calc(100vw - 32px)', sm: 440 },
    maxWidth: '100%',
    bgcolor: 'background.paper',
    border: '1px solid rgba(148, 163, 184, 0.32)',
    borderRadius: '8px',
    boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
    outline: 'none',
    p: { xs: 2, sm: 3 },
}

const modalStyleLarge = {
    ...modalStyle,
    width: { xs: 'calc(100vw - 32px)', sm: 600 },
}

const formatPhone = (phoneNumber) => {
    if (!phoneNumber) return ''
    const cleaned = phoneNumber.replace(/\D/g, '')
    
    if (cleaned.length === 11) {
        return cleaned.replace(/^(\d{3})(\d{4})(\d{4})$/, '$1-$2-$3')
    } else if (cleaned.length === 10) {
        return cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3')
    }
    return phoneNumber
}

const createShippingDialogValues = (overrides = {}) => ({
    orderDetailId: null,
    shippingNumber: '',
    shippingCompany: '롯데택배',
    shippingStatus: CONSTANTS.SHIPPING_STATUS.SHIPPING,
    ...overrides,
})

const createAddressModalData = (overrides = {}) => ({
    paymentId: null,
    buyerAddress: '',
    buyerZipcode: '',
    zipcode: '',
    address: '',
    addressDetail: '',
    ...overrides,
})

// 주소 수정 모달은 row 원본 주소와 입력 중인 신규 주소 draft를 함께 관리한다.
const createAddressModalState = (overrides = {}) => {
    const nextData = createAddressModalData(overrides.data)

    return {
        open: false,
        postcodeOpen: false,
        error: false,
        errorText: '*주소는 필수 입력 사항입니다.',
        addressSelected: Boolean(nextData.zipcode || nextData.address),
        data: nextData,
        ...overrides,
    }
}

const getProductCategoryDisplayName = (productCategory) => {
    if (!productCategory) return '-'
    if (productCategory.animal_package_region?.regionName) return productCategory.animal_package_region.regionName
    if (Number(productCategory.status) === 1) return productCategory.name || '-'
    if (Number(productCategory.product?.visibleStatus) === 1) return productCategory.name || '-'
    if (productCategory.distance === null || productCategory.distance === undefined || productCategory.distance === '') return '-'
    if (Number(productCategory.distance) === 0 && productCategory.name) return productCategory.name

    return `${productCategory.distance}km`
}

const getOrderItemDisplayName = (row) => {
    const categoryName = getProductCategoryDisplayName(row.product_category)
    const optionName = row.product_option?.name

    return optionName ? `${categoryName}, ${optionName}` : categoryName
}

const getShippingReceiverName = (row) => row.order?.user_shipping?.username || row.order?.payment?.buyerName || ''
const getShippingReceiverPhone = (row) => row.order?.user_shipping?.phone || row.order?.payment?.buyerPhone || ''

export default function OrderShippings({ adminId, role }) {
    const currentYear = new Date().getFullYear()
    const minYear = currentYear - CONSTANTS.MIN_YEAR_OFFSET
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: {
            ...DEFAULT_SEARCH_SELECT,
        },
        selectParams: {
            searchShippingStatus: 'searchShippingStatus',
            searchPayStatus: 'searchPayStatus',
        },
        getExtraState: ({ locationState, searchParams }) => {
            const dialogType = searchParams.get('dialog')
            const shippingDraftNumber = searchParams.has('shippingNumberDraft') ? (searchParams.get('shippingNumberDraft') || '') : null
            const shippingDraftCompany = searchParams.has('shippingCompanyDraft') ? (searchParams.get('shippingCompanyDraft') || '') : null
            const shippingDraftStatus = searchParams.has('shippingStatusDraft')
                ? parseNumberParam(searchParams.get('shippingStatusDraft'), CONSTANTS.SHIPPING_STATUS.SHIPPING)
                : null
            const addressDraftZipcode = searchParams.has('addressZipcode') ? (searchParams.get('addressZipcode') || '') : null
            const addressDraftValue = searchParams.has('addressDraft') ? (searchParams.get('addressDraft') || '') : null
            const addressDraftDetail = searchParams.has('addressDetailDraft') ? (searchParams.get('addressDetailDraft') || '') : null

            return {
                startTime: parseDateParam(searchParams.get('start'), locationState.startTime || new Date()),
                endTime: parseDateParam(searchParams.get('end'), locationState.endTime || new Date()),
                shippingDialogOpen: dialogType === 'shipping',
                shippingDialogOrderDetailId: parseNumberParam(searchParams.get('shippingOrderDetailId'), null),
                shippingDraftNumber,
                shippingDraftCompany,
                shippingDraftStatus,
                addressDialogOpen: dialogType === 'address' || dialogType === 'address-postcode',
                addressPostcodeOpen: dialogType === 'address-postcode',
                addressDialogPaymentId: parseNumberParam(searchParams.get('addressPaymentId'), null),
                addressDraftZipcode,
                addressDraftValue,
                addressDraftDetail,
                addressDraftSelected: searchParams.has('addressSelected')
                    ? searchParams.get('addressSelected') === '1'
                    : Boolean(addressDraftZipcode || addressDraftValue),
            }
        },
    }))
    const initialListState = initialListStateRef.current
    const shippingDialogHydratedRef = useRef(false)
    const addressDialogHydratedRef = useRef(false)
    
    // 상태 관리
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    
    // Snackbar 상태
    const [snackbar, setSnackbar] = useState({
        open: initialListState.snackbar.openSnackbar || false,
        severity: initialListState.snackbar.severitySnackbar || 'info',
        message: initialListState.snackbar.messageSnackbar || ''
    })
    
    // 검색 상태
    const [searchText, setSearchText] = useState(initialListState.searchText || '')
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery || '')
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    
    // 날짜 상태 (안전한 초기화)
    const [startTime, setStartTime] = useState(initialListState.startTime)
    const [endTime, setEndTime] = useState(initialListState.endTime)
    
    // 다이얼로그 상태
    const [dialogOpen, setDialogOpen] = useState(Boolean(initialListState.shippingDialogOpen))
    const [shippingSaving, setShippingSaving] = useState(false)
    const [shippingActionLoading, setShippingActionLoading] = useState(null)
    const [dialogValues, setDialogValues] = useState(createShippingDialogValues({
        orderDetailId: initialListState.shippingDialogOrderDetailId,
        shippingNumber: initialListState.shippingDraftNumber || '',
        shippingCompany: initialListState.shippingDraftCompany || '롯데택배',
        shippingStatus: initialListState.shippingDraftStatus || CONSTANTS.SHIPPING_STATUS.SHIPPING,
    }))
    
    // 주소 모달 상태
    const [addressSaving, setAddressSaving] = useState(false)
    const [addressModal, setAddressModal] = useState(createAddressModalState({
        open: Boolean(initialListState.addressDialogOpen),
        postcodeOpen: Boolean(initialListState.addressPostcodeOpen),
        addressSelected: Boolean(initialListState.addressDraftSelected),
        data: {
            paymentId: initialListState.addressDialogPaymentId,
            buyerAddress: '',
            buyerZipcode: '',
            zipcode: initialListState.addressDraftZipcode || '',
            address: initialListState.addressDraftValue || '',
            addressDetail: initialListState.addressDraftDetail || '',
        }
    }))
    
    // Ref for cleanup
    const fileReaderRef = useRef(null)
    const shippingNumberInputRef = useRef(null)
    const [excelActionLoading, setExcelActionLoading] = useState(null)
    const leaveGuardEnabled = hasPendingOrderShippingExcelUpload(excelActionLoading)

    usePendingOrderShippingExcelUploadUnloadGuard(leaveGuardEnabled)
    
    // 데이터 가져오기 (useCallback으로 메모이제이션)
    const getData = useCallback(async () => {
        setLoading(true)
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/orderShipping/getOrderShippingList', { 
                page: curPage, 
                offset: CONSTANTS.OFFSET, 
                adminId, 
                role,
                searchText: searchQuery,
                searchSelect, 
                startTime: formatDate(startTime), 
                endTime: formatDate(endTime) 
            })
            
            if (data.code === 1) {
                setTotalCount(data.data?.count || 0)
                setData(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '배송 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                showSnackbar('error', errorMessage)
            }
        } catch (error) {
            console.error('데이터 조회 오류:', error)
            const errorMessage = '배송 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            showSnackbar('error', errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, adminId, role, searchQuery, searchSelect, startTime, endTime, history])

    // Snackbar 헬퍼
    const showSnackbar = (severity, message) => {
        setSnackbar({ open: true, severity, message })
    }

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }))
    }

    // Effects
    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const activeDialogType = dialogOpen && dialogValues.orderDetailId
            ? 'shipping'
            : addressModal.open && addressModal.data.paymentId
                ? (addressModal.postcodeOpen ? 'address-postcode' : 'address')
                : ''

        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: {
                searchShippingStatus: 'searchShippingStatus',
                searchPayStatus: 'searchPayStatus',
            },
            extraParams: [
                {
                    param: 'start',
                    value: startTime,
                    serialize: formatDate,
                    shouldInclude: () => true,
                },
                {
                    param: 'end',
                    value: endTime,
                    serialize: formatDate,
                    shouldInclude: () => true,
                },
                {
                    param: 'dialog',
                    value: activeDialogType,
                    shouldInclude: () => Boolean(activeDialogType),
                },
                {
                    param: 'shippingOrderDetailId',
                    value: dialogValues.orderDetailId,
                    shouldInclude: (value) => activeDialogType === 'shipping' && Boolean(value),
                },
                {
                    param: 'shippingStatusDraft',
                    value: dialogValues.shippingStatus,
                    shouldInclude: () => activeDialogType === 'shipping',
                },
                {
                    param: 'shippingCompanyDraft',
                    value: dialogValues.shippingCompany,
                    shouldInclude: () => activeDialogType === 'shipping',
                },
                {
                    param: 'shippingNumberDraft',
                    value: dialogValues.shippingNumber,
                    shouldInclude: () => activeDialogType === 'shipping',
                },
                {
                    param: 'addressPaymentId',
                    value: addressModal.data.paymentId,
                    shouldInclude: (value) => (activeDialogType === 'address' || activeDialogType === 'address-postcode') && Boolean(value),
                },
                {
                    param: 'addressZipcode',
                    value: addressModal.data.zipcode,
                    shouldInclude: () => (activeDialogType === 'address' || activeDialogType === 'address-postcode') && Boolean(addressModal.data.zipcode),
                },
                {
                    param: 'addressDraft',
                    value: addressModal.data.address,
                    shouldInclude: () => (activeDialogType === 'address' || activeDialogType === 'address-postcode') && Boolean(addressModal.data.address),
                },
                {
                    param: 'addressDetailDraft',
                    value: addressModal.data.addressDetail,
                    shouldInclude: () => (activeDialogType === 'address' || activeDialogType === 'address-postcode') && Boolean(addressModal.data.addressDetail),
                },
                {
                    param: 'addressSelected',
                    value: addressModal.addressSelected ? 1 : '',
                    shouldInclude: () => (activeDialogType === 'address' || activeDialogType === 'address-postcode') && addressModal.addressSelected,
                },
            ],
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [addressModal.addressSelected, addressModal.data.address, addressModal.data.addressDetail, addressModal.data.paymentId, addressModal.data.zipcode, addressModal.open, addressModal.postcodeOpen, curPage, dialogOpen, dialogValues.orderDetailId, dialogValues.shippingCompany, dialogValues.shippingNumber, dialogValues.shippingStatus, endTime, history, location.pathname, location.search, location.state, searchQuery, searchSelect, startTime])

    // 클린업
    useEffect(() => {
        return () => {
            if (fileReaderRef.current) {
                fileReaderRef.current.abort()
            }
        }
    }, [])

    useEffect(() => {
        if (!dialogOpen) {
            return
        }

        if (dialogValues.shippingStatus === CONSTANTS.SHIPPING_STATUS.PREPARING) {
            return
        }

        const focusTimer = window.setTimeout(() => {
            shippingNumberInputRef.current?.focus()
        }, 0)

        return () => window.clearTimeout(focusTimer)
    }, [dialogOpen, dialogValues.shippingStatus, dialogValues.shippingNumber])

    // 새로고침으로 복귀했을 때 현재 페이지 데이터에서 다시 찾아 같은 송장 다이얼로그를 연다.
    useEffect(() => {
        if (loading) {
            return
        }

        if (shippingDialogHydratedRef.current || !initialListState.shippingDialogOpen || !initialListState.shippingDialogOrderDetailId) {
            return
        }

        const targetRow = data.find((row) => Number(row.orderDetailId) === Number(initialListState.shippingDialogOrderDetailId))

        if (!targetRow) {
            shippingDialogHydratedRef.current = true
            setDialogValues(createShippingDialogValues())
            setDialogOpen(false)
            return
        }

        shippingDialogHydratedRef.current = true
        setDialogOpen(true)
        setDialogValues(createShippingDialogValues({
            orderDetailId: targetRow.orderDetailId,
            shippingStatus: initialListState.shippingDraftStatus ?? (
                targetRow.shippingNumber
                    ? (targetRow.shippingStatus || CONSTANTS.SHIPPING_STATUS.SHIPPING)
                    : CONSTANTS.SHIPPING_STATUS.SHIPPING
            ),
            shippingNumber: initialListState.shippingDraftNumber ?? (targetRow.shippingNumber || ''),
            shippingCompany: initialListState.shippingDraftCompany ?? (targetRow.shippingCompany || '롯데택배'),
        }))
    }, [data, initialListState.shippingDialogOpen, initialListState.shippingDialogOrderDetailId, initialListState.shippingDraftCompany, initialListState.shippingDraftNumber, initialListState.shippingDraftStatus, loading])

    // 새로고침으로 복귀했을 때 현재 페이지 데이터에서 다시 찾아 같은 주소 수정 단계까지 복원한다.
    useEffect(() => {
        if (loading) {
            return
        }

        if (addressDialogHydratedRef.current || !initialListState.addressDialogOpen || !initialListState.addressDialogPaymentId) {
            return
        }

        const targetRow = data.find((row) => Number(row.order?.payment?.paymentId) === Number(initialListState.addressDialogPaymentId))

        if (!targetRow) {
            addressDialogHydratedRef.current = true
            setAddressModal(createAddressModalState())
            return
        }

        addressDialogHydratedRef.current = true
        setAddressModal(createAddressModalState({
            open: true,
            postcodeOpen: Boolean(initialListState.addressPostcodeOpen),
            addressSelected: Boolean(initialListState.addressDraftSelected),
            data: {
                paymentId: targetRow.order.payment.paymentId,
                buyerZipcode: targetRow.order.payment.buyerZipcode,
                buyerAddress: targetRow.order.payment.buyerAddress,
                zipcode: initialListState.addressDraftZipcode || '',
                address: initialListState.addressDraftValue || '',
                addressDetail: initialListState.addressDraftDetail || '',
            }
        }))
    }, [data, initialListState.addressDialogOpen, initialListState.addressDialogPaymentId, initialListState.addressDraftDetail, initialListState.addressDraftSelected, initialListState.addressDraftValue, initialListState.addressDraftZipcode, initialListState.addressPostcodeOpen, loading])

    // 검색 핸들러
    const handleSearch = () => {
        if (loading || excelActionLoading || isActionBusy) {
            return
        }

        const nextSearchQuery = searchText.trim()

        if (curPage === 1 && searchQuery === nextSearchQuery) {
            getData()
            return
        }

        setSearchQuery(nextSearchQuery)
        setCurPage(1)
    }

    const handleSearchSelectChange = (e) => {
        if (loading || excelActionLoading || isActionBusy) {
            return
        }

        setSearchSelect(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
        setCurPage(1)
    }

    const handleStartTimeChange = (value) => {
        if (loading || excelActionLoading || isActionBusy || !value) {
            return
        }

        setStartTime(value)
        setCurPage(1)
    }

    const handleEndTimeChange = (value) => {
        if (loading || excelActionLoading || isActionBusy || !value) {
            return
        }

        setEndTime(value)
        setCurPage(1)
    }

    const isActionBusy = shippingSaving || addressSaving
    const isExcelBusy = Boolean(excelActionLoading)
    const isShippingResetting = shippingActionLoading === 'reset'
    const isShippingUpdating = shippingActionLoading === 'update'
    const isEpostDownloadBusy = excelActionLoading === 'download-epost'
    const isLotteDownloadBusy = excelActionLoading === 'download-lotte'
    const isEpostUploadBusy = excelActionLoading === 'upload-epost'
    const isLotteUploadBusy = excelActionLoading === 'upload-lotte'
    const isPageBusy = loading || isActionBusy || isExcelBusy

    // 송장번호 다이얼로그는 닫을 때 draft/query도 같이 정리한다.
    const closeShippingDialog = () => {
        if (shippingSaving) {
            return
        }

        setDialogValues(createShippingDialogValues())
        setDialogOpen(false)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        handleSearch()
    }

    const resetFilters = () => {
        const today = formatDate(new Date())
        const isInitialState = curPage === 1
            && !searchText
            && !searchQuery
            && !searchSelect.searchShippingStatus
            && !searchSelect.searchPayStatus
            && formatDate(startTime) === today
            && formatDate(endTime) === today

        if (isInitialState) {
            getData()
            return
        }

        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
        setStartTime(new Date())
        setEndTime(new Date())
        setCurPage(1)
    }

    // 송장번호 다이얼로그
    const openShippingDialog = (orderDetailId, shippingStatus = 2, shippingNumber = '', shippingCompany = '롯데택배') => {
        if (isActionBusy) {
            return
        }

        const isNewRegistration = !shippingNumber

        setDialogValues(createShippingDialogValues({
            orderDetailId,
            shippingStatus: isNewRegistration
                ? CONSTANTS.SHIPPING_STATUS.SHIPPING
                : (shippingStatus || CONSTANTS.SHIPPING_STATUS.SHIPPING),
            shippingNumber: shippingNumber || '',
            shippingCompany: shippingCompany || '롯데택배'
        }))
        setDialogOpen(true)
    }

    const handleDialogFieldChange = (e) => {
        if (shippingSaving) {
            return
        }

        setDialogValues(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const updateShippingNumber = async () => {
        if (shippingSaving) {
            return
        }

        const normalizedShippingNumber = normalizeShippingNumber(dialogValues.shippingNumber)

        // 상품준비중이 아닌 경우에만 송장번호 체크
        if (dialogValues.shippingStatus !== CONSTANTS.SHIPPING_STATUS.PREPARING) {
            if (!normalizedShippingNumber) {
                showSnackbar('warning', '송장번호를 입력해주세요.')
                return
            }

            if (!isValidShippingNumber(dialogValues.shippingNumber)) {
                showSnackbar('warning', SHIPPING_NUMBER_ERROR_MESSAGE)
                return
            }
        }

        try {
            setShippingSaving(true)
            setShippingActionLoading('update')
            const payload = {
                orderDetailId: dialogValues.orderDetailId, 
                shippingStatus: dialogValues.shippingStatus,
            }

            // 상품준비중인 경우 택배사와 송장번호를 null로 전송
            if (dialogValues.shippingStatus === CONSTANTS.SHIPPING_STATUS.PREPARING) {
                payload.shippingNumber = null
                payload.shippingCompany = null
            } else {
                payload.shippingNumber = normalizedShippingNumber
                payload.shippingCompany = dialogValues.shippingCompany
            }

            const { data } = await API.post('/api/v1/admin/orderShipping/updateShippingNumber', payload)
            
            if (data.code === 1) {
                setDialogValues(createShippingDialogValues())
                showSnackbar('success', data.message)
                setDialogOpen(false)
                getData()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', data.message)
            }
        } catch (error) {
            console.error('송장번호 업데이트 오류:', error)
            showSnackbar('error', '송장번호 업데이트에 실패했습니다.')
        } finally {
            setShippingActionLoading(null)
            setShippingSaving(false)
        }
    }

    const resetShippingNumber = async () => {
        if (shippingSaving) {
            return
        }

        if (!window.confirm('송장번호를 초기화하시겠습니까? (상품준비중 상태로 변경됩니다)')) {
            return
        }

        try {
            setShippingSaving(true)
            setShippingActionLoading('reset')
            const { data } = await API.post('/api/v1/admin/orderShipping/updateShippingNumber', { 
                orderDetailId: dialogValues.orderDetailId, 
                shippingStatus: CONSTANTS.SHIPPING_STATUS.PREPARING, 
                shippingNumber: null, 
                shippingCompany: null 
            })
            
            if (data.code === 1) {
                setDialogValues(createShippingDialogValues())
                showSnackbar('success', '송장번호가 초기화되었습니다.')
                setDialogOpen(false)
                getData()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', data.message)
            }
        } catch (error) {
            console.error('송장번호 초기화 오류:', error)
            showSnackbar('error', '송장번호 초기화에 실패했습니다.')
        } finally {
            setShippingActionLoading(null)
            setShippingSaving(false)
        }
    }

    // 엑셀 다운로드
    const exportToExcel = async (fileName, type) => {
        if (excelActionLoading) {
            return
        }

        const nextAction = `download-${type}`

        try {
            setExcelActionLoading(nextAction)
            const { data } = await API.post('/api/v1/admin/orderShipping/getExcelOrderShippingList', { 
                searchText: searchQuery,
                searchSelect,
                startTime: formatDate(startTime), 
                endTime: formatDate(endTime), 
                type 
            })
            
            if (data.code === 1) {
                const jsonArr = data.data.map(row => {
                    const receiverName = getShippingReceiverName(row)
                    const receiverPhone = getShippingReceiverPhone(row)
                    
                    return {
                        etc1: row.order.payment.pgTime,
                        상품주문번호: row.orderDetailId,
                        etc2: '', etc3: '', etc4: '', etc5: '', etc6: '', etc7: '', etc8: '',
                        주문자명: row.order.payment.buyerName,
                        etc9: '',
                        주문자이동통신: formatPhone(row.order.payment.buyerPhone),
                        etc10: '', etc11: '', etc12: '', etc13: '', etc14: '', etc15: '', etc16: '',
                        상품명: row.product_category.product.title,
                        상품모델: getOrderItemDisplayName(row),
                        etc17: '',
                        수량: row.quantity,
                        상품금액: row.product_category.price,
                        결제금액: row.payAmount,
                        etc20: '', etc21: '', etc22: '', etc23: '', etc24: '', etc25: '', etc26: '', 
                        etc27: '', etc28: '', etc29: '', etc30: '', etc31: '', etc32: '', etc33: '', 
                        etc34: '', etc35: '', etc36: '',
                        배송메세지: row.order.shippingMemo,
                        etc37: '', etc38: '',
                        수취인명: receiverName,
                        수취인이동통신: receiverPhone,
                        etc39: '',
                        수취인우편번호: row.order.payment.buyerZipcode,
                        수취인주소: row.order.payment.buyerAddress,
                        etc40: '', etc41: '', etc42: '', etc43: '', etc44: '',
                    }
                })
                
                if (jsonArr.length > 0) {
                    const ws = XLS.utils.json_to_sheet(jsonArr)
                    const wb = { Sheets: { data: ws }, SheetNames: ['data'] }
                    const excelBuffer = XLS.write(wb, { bookType: 'xlsx', type: 'array' })
                    const excelData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' })
                    const now = new Date()
                    FileSaver.saveAs(excelData, `${fileName}-${formatDate(now)}.xlsx`)
                } else {
                    showSnackbar('error', '엑셀 데이터가 없습니다.')
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', data.message)
            }
        } catch (error) {
            console.error('엑셀 다운로드 오류:', error)
            showSnackbar('error', '엑셀 다운로드에 실패했습니다.')
        } finally {
            setExcelActionLoading(null)
        }
    }

    // 엑셀 업로드
    const handleFileUpload = (e, type) => {
        if (excelActionLoading) {
            e.target.value = ''
            return
        }

        e.preventDefault()
        
        const file = e.target.files?.[0]
        if (!file) {
            showSnackbar('warning', '파일을 선택해주세요.')
            return
        }

        if (!isShippingExcelFile(file)) {
            e.target.value = ''
            showSnackbar('error', SHIPPING_EXCEL_FILE_ERROR_MESSAGE)
            return
        }
        
        const reader = new FileReader()
        fileReaderRef.current = reader
        
        reader.onloadend = () => {
            uploadShippingExcel(e, file, type)
        }
        
        reader.onerror = () => {
            showSnackbar('error', '파일 읽기에 실패했습니다.')
        }
    
        reader.readAsDataURL(file)
    }

    const uploadShippingExcel = async (e, file, type) => {
        if (excelActionLoading) {
            return
        }

        const nextAction = `upload-${type}`

        try {
            setExcelActionLoading(nextAction)
            const formData = new FormData()
            formData.append('excelFile', file)

            const apiName = type === 'lotte' ? 'updateShippingNumberExcelLotte' : 'updateShippingNumberExcel'

            const { data } = await API({
                method: 'post',
                url: `/api/v1/admin/orderShipping/${apiName}`,
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })
            
            if (data.code === 1) {
                const updatedCount = Number(data.data?.updatedCount || 0)
                const skippedCount = Number(data.data?.skippedCount || 0)
                const successMessage = data.data
                    ? `업로드 완료: 반영 ${updatedCount}건, 스킵 ${skippedCount}건`
                    : data.message

                showSnackbar('success', successMessage)
                e.target.value = ''
                getData()
            } else if (data.code === 403) {
                setExcelActionLoading(null)
                setTimeout(() => {
                    history.push('/login')
                }, 0)
            } else {
                showSnackbar('error', data.message)
            }
        } catch (error) {
            console.error('엑셀 업로드 오류:', error)
            showSnackbar('error', '엑셀 업로드에 실패했습니다.')
        } finally {
            if (e?.target) {
                e.target.value = ''
            }
            setExcelActionLoading(null)
        }
    }

    // 주소 관련 핸들러
    const handleAddressModify = (row) => {
        if (isActionBusy) {
            return
        }

        setAddressModal(createAddressModalState({
            open: true,
            postcodeOpen: false,
            data: {
                paymentId: row.order.payment.paymentId,
                buyerZipcode: row.order.payment.buyerZipcode,
                buyerAddress: row.order.payment.buyerAddress,
                zipcode: '',
                address: '',
                addressDetail: '',
            }
        }))
    }

    const handlePostcodeComplete = (res) => {
        let fullAddress = res.address
        let extraAddress = ""

        if (res.addressType === "R") {
            if (res.bname) extraAddress += res.bname
            if (res.buildingName) {
                extraAddress += extraAddress ? `, ${res.buildingName}` : res.buildingName
            }
            fullAddress += extraAddress ? ` (${extraAddress})` : ""
        }
        
        setAddressModal(prev => ({
            ...prev,
            postcodeOpen: false,
            addressSelected: true,
            data: {
                ...prev.data,
                zipcode: res.zonecode,
                address: fullAddress
            }
        }))
    }

    const handleAddressUpdate = async () => {
        if (addressSaving) {
            return
        }

        if (!addressModal.data.address || !addressModal.data.addressDetail) {
            setAddressModal(prev => ({ ...prev, error: true }))
            return
        }

        try {
            setAddressSaving(true)
            const { data } = await API.post('/api/v1/admin/orderShipping/update', addressModal.data)
            
            if (data.code === 1) {
                setAddressModal(createAddressModalState())
                getData()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                showSnackbar('error', data.message)
            }
        } catch (error) {
            console.error('주소 업데이트 오류:', error)
            showSnackbar('error', '주소 업데이트에 실패했습니다.')
        } finally {
            setAddressSaving(false)
        }
    }

    const isCancelledOrder = (row) => {
        return row?.payStatus === CONSTANTS.PAY_STATUS.CANCELLED
    }

    const canEditShippingNumber = (row) => {
        return !isCancelledOrder(row) && !isPageBusy
    }

    const openShippingDialogForRow = (row) => {
        if (!canEditShippingNumber(row)) {
            return
        }

        openShippingDialog(
            row.orderDetailId,
            row.shippingStatus,
            row.shippingNumber,
            row.shippingCompany
        )
    }

    const renderShippingNumberAction = (row) => {
        const disabled = !canEditShippingNumber(row)
        const hasShippingNumber = Boolean(row.shippingNumber)
        const label = row.shippingNumber || (isCancelledOrder(row) ? '취소 주문' : '송장 등록')

        return (
            <Button
                type="button"
                size="small"
                variant={hasShippingNumber ? 'outlined' : 'contained'}
                color={hasShippingNumber ? 'primary' : 'secondary'}
                startIcon={!hasShippingNumber && !isCancelledOrder(row) ? <AddCircleOutlineIcon /> : undefined}
                disabled={disabled}
                onClick={() => openShippingDialogForRow(row)}
                sx={{
                    minWidth: hasShippingNumber ? 92 : 96,
                    height: 30,
                    px: 1,
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Button>
        )
    }

    // 배송 목록은 기본 날짜가 오늘이므로 검색어, 상태 필터, 날짜 변경 여부를 함께 본다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        const hasChangedSelect = Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? ''

            return String(currentValue) !== String(defaultValue)
        })

        if (hasChangedSelect) {
            return true
        }

        const todayText = formatDate(new Date())

        return formatDate(startTime) !== todayText || formatDate(endTime) !== todayText
    }

    // 테이블 행이 없을 때 운영자가 오류와 정상 빈 목록을 구분할 수 있게 표시한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="배송 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={getData} disabled={isPageBusy}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 배송 내역이 없습니다."
                    description="검색어, 날짜 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isPageBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 배송 내역이 없습니다."
                description="배송 요청이 발생하면 이 목록에 표시됩니다."
            />
        )
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Prompt when={leaveGuardEnabled} message={ORDER_SHIPPING_EXCEL_UPLOAD_LEAVE_MESSAGE} />
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                {/* 주소 수정 모달 */}
                <Modal open={addressModal.open} onClose={addressSaving ? undefined : () => setAddressModal(createAddressModalState())} aria-label="배송 주소 수정">
                    <Box sx={modalStyleLarge}>
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12}>배송주소</Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="배송주소"
                                    value={`(${addressModal.data.buyerZipcode}) ${addressModal.data.buyerAddress}`}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>신규주소</Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="우편번호"
                                    value={addressModal.data.zipcode}
                                    size="small"
                                    fullWidth
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="주소"
                                    value={addressModal.data.address}
                                    size="small"
                                    fullWidth
                                    onClick={() => {
                                        if (addressSaving) {
                                            return
                                        }

                                        setAddressModal(prev => ({ ...prev, postcodeOpen: true }))
                                    }}
                                    InputProps={{ 
                                        readOnly: true, 
                                        endAdornment: <SearchIcon color="action"/>
                                    }}
                                    disabled={addressSaving}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="상세주소"
                                    value={addressModal.data.addressDetail}
                                    onChange={(e) => setAddressModal(prev => ({
                                        ...prev,
                                        data: { ...prev.data, addressDetail: e.target.value }
                                    }))}
                                    size="small"
                                    fullWidth
                                    disabled={!addressModal.addressSelected || addressSaving}
                                />
                            </Grid>
                            {addressModal.error && (
                                <Grid item xs={12}>
                                    <span style={{ color: 'red', fontSize: '13px'}}>
                                        {addressModal.errorText}
                                    </span>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="center" gap={2}>
                                    <Button 
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<CancelIcon />}
                                        sx={FORM_DIALOG_ACTION_BUTTON_SX}
                                        disabled={addressSaving}
                                        onClick={() => setAddressModal(createAddressModalState())}
                                    >
                                        닫기
                                    </Button>
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        startIcon={addressSaving ? BUTTON_LOADING_ICON : <SendIcon />}
                                        aria-busy={addressSaving}
                                        sx={FORM_DIALOG_ACTION_BUTTON_SX}
                                        disabled={addressSaving}
                                        onClick={handleAddressUpdate}
                                    >
                                        {addressSaving ? '저장 중...' : '저장'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Modal>

                {/* 우편번호 검색 모달 */}
                <AddressSearchModal
                    open={addressModal.postcodeOpen}
                    onClose={addressSaving ? undefined : () => setAddressModal(prev => ({ ...prev, postcodeOpen: false }))}
                    onComplete={handlePostcodeComplete}
                />

                {/* 타이틀 */}
                <Title>배송 리스트</Title>

                {/* 검색폼 */}
                <ListFilterBar>
                    <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isPageBusy} />}>
                        <InputBase
                            sx={LIST_FILTER_SEARCH_FIELD_SX}
                            placeholder="주문번호/송장번호/구매자/상품명/userId"
                            type="search" 
                            value={searchText}
                            disabled={isPageBusy}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        <IconButton 
                            sx={LIST_FILTER_SEARCH_BUTTON_SX}
                            aria-label="검색" 
                            type="submit"
                            disabled={isPageBusy}
                        >
                            <SearchIcon />
                        </IconButton>
                        <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                            <DesktopDatePicker
                                label="시작일"
                                inputFormat="yyyy-MM-dd"
                                mask={"____-__-__"}
                                minDate={new Date(minYear, 0, 1)}
                                maxDate={new Date()}
                                value={startTime}
                                disabled={isPageBusy}
                                onChange={handleStartTimeChange}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        size="small" 
                                        disabled={isPageBusy}
                                        sx={LIST_FILTER_DATE_FIELD_SX}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                        &nbsp;~&nbsp;
                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                            <DesktopDatePicker
                                label="종료일"
                                inputFormat="yyyy-MM-dd"
                                mask={"____-__-__"}
                                value={endTime}
                                minDate={startTime}
                                maxDate={new Date()}
                                disabled={isPageBusy}
                                onChange={handleEndTimeChange}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        size="small" 
                                        disabled={isPageBusy}
                                        sx={LIST_FILTER_DATE_FIELD_SX}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                        <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                        <TextField
                            label="배송상태"
                            select
                            name="searchShippingStatus"
                            value={searchSelect.searchShippingStatus}
                            onChange={handleSearchSelectChange}
                            disabled={isPageBusy}
                            size="small"
                            sx={LIST_FILTER_SELECT_FIELD_SX}
                        >
                            <MenuItem value=''>전체</MenuItem>
                            <MenuItem value={CONSTANTS.SHIPPING_STATUS.PREPARING}>상품준비중</MenuItem>
                            <MenuItem value={CONSTANTS.SHIPPING_STATUS.SHIPPING}>배송중</MenuItem>
                            <MenuItem value={CONSTANTS.SHIPPING_STATUS.COMPLETED}>배송완료</MenuItem>
                        </TextField>
                        <TextField
                            label="결제상태"
                            select
                            name="searchPayStatus"
                            value={searchSelect.searchPayStatus}
                            onChange={handleSearchSelectChange}
                            disabled={isPageBusy}
                            size="small"
                            sx={LIST_FILTER_SELECT_FIELD_SX}
                        >
                            <MenuItem value=''>전체</MenuItem>
                            <MenuItem value={CONSTANTS.PAY_STATUS.CANCELLED}>취소</MenuItem>
                            <MenuItem value={CONSTANTS.PAY_STATUS.PAID}>결제</MenuItem>
                        </TextField>
                    </ListFilterForm>

                </ListFilterBar>

                {/* 엑셀 다운로드/업로드 버튼 */}
                <ListTableToolbar
                    left={(
                        <>
                            <Button
                                color="success"
                                variant="contained"
                                onClick={() => exportToExcel('랭킹마라톤_신규주문_우체국택배', 'epost')}
                                disabled={isPageBusy}
                                size="small"
                                startIcon={isEpostDownloadBusy ? BUTTON_LOADING_ICON : <Download />}
                                aria-busy={isEpostDownloadBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                {isEpostDownloadBusy ? '다운로드 중...' : '다운로드(우체국)'}
                            </Button>
                            <FormControl>
                                <TextField
                                    inputProps={{ accept: SHIPPING_EXCEL_FILE_ACCEPT }}
                                    style={{ display: 'none' }}
                                    name="excelFile"
                                    id="excelFile"
                                    type="file"
                                    disabled={isPageBusy}
                                    onChange={e => handleFileUpload(e, 'epost')}
                                />
                                <Box component="label" htmlFor="excelFile" sx={{ display: 'inline-flex' }}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        component="span"
                                        disabled={isPageBusy}
                                        startIcon={isEpostUploadBusy ? BUTTON_LOADING_ICON : <UploadFileIcon />}
                                        aria-busy={isEpostUploadBusy}
                                        size="small"
                                        sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                    >
                                        {isEpostUploadBusy ? '업로드 중...' : '업로드(우체국)'}
                                    </Button>
                                </Box>
                            </FormControl>
                        </>
                    )}
                    right={(
                        <>
                            <Button
                                color="success"
                                variant="contained"
                                onClick={() => exportToExcel('랭킹마라톤_신규주문_롯데택배', 'lotte')}
                                disabled={isPageBusy}
                                size="small"
                                startIcon={isLotteDownloadBusy ? BUTTON_LOADING_ICON : <Download />}
                                aria-busy={isLotteDownloadBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                {isLotteDownloadBusy ? '다운로드 중...' : '다운로드(롯데택배)'}
                            </Button>

                            <FormControl>
                                <TextField
                                    inputProps={{ accept: SHIPPING_EXCEL_FILE_ACCEPT }}
                                    style={{ display: 'none' }}
                                    name="excelFileLotte"
                                    id="excelFileLotte"
                                    type="file"
                                    disabled={isPageBusy}
                                    onChange={e => handleFileUpload(e, 'lotte')}
                                />
                                <Box component="label" htmlFor="excelFileLotte" sx={{ display: 'inline-flex' }}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        component="span"
                                        disabled={isPageBusy}
                                        startIcon={isLotteUploadBusy ? BUTTON_LOADING_ICON : <UploadFileIcon />}
                                        aria-busy={isLotteUploadBusy}
                                        size="small"
                                        sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                    >
                                        {isLotteUploadBusy ? '업로드 중...' : '업로드(롯데택배)'}
                                    </Button>
                                </Box>
                            </FormControl>
                        </>
                    )}
                />

                {/* 테이블 */}
                <ListTableContainer sx={{ opacity: leaveGuardEnabled ? 0.6 : 1, pointerEvents: leaveGuardEnabled ? 'none' : 'auto' }}>
                    <Table size="small" sx={{ minWidth: 1200 }}>
                        <caption>Total Count: {formatNumber(totalCount)}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell align="center">상품주문번호</TableCell>
                                <TableCell align="center">송장번호</TableCell>
                                <TableCell align="center" sx={{ width: '10%' }}>배송상태</TableCell>
                                <TableCell align="center">결제상태<br/>결제금액</TableCell>
                                <TableCell align="center">상품명<br/>옵션명<br/>수량</TableCell>
                                <TableCell align="center" sx={{ minWidth: 180, whiteSpace: 'nowrap' }}>
                                    구매자명/구매자연락처<br/>수취인명/수취인연락처
                                </TableCell>
                                <TableCell sx={{ width: '18%' }} align="center">수취인주소</TableCell>
                                <TableCell sx={{ width: '12%' }}>배송요청사항</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? data.map((row) => (
                                <TableRow key={row.orderDetailId}>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>{row.orderDetailId}</TableCell>
                                    <TableCell align="center">
                                        {renderShippingNumberAction(row)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <StatusChip status={row.shippingStatus} config={SHIPPING_STATUS_CHIP} />
                                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: 12 }}>
                                            {row.shippingCompany || '-'}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip status={row.payStatus} config={PAYMENT_STATUS_CHIP} />
                                        <br/>
                                        {formatNumber(row.payAmount)}
                                    </TableCell>
                                    <TableCell>
                                        {row.product_category.product.title}<br/>
                                        {getOrderItemDisplayName(row)}
                                        <br/>
                                        {row.quantity}개
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 180, whiteSpace: 'nowrap' }}>
                                        <Box>
                                            {row.order.user.username} {formatPhone(row.order.user.phone)}
                                        </Box>
                                        <Box sx={{ mt: 1 }}>
                                            {getShippingReceiverName(row)} {formatPhone(getShippingReceiverPhone(row))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {row.shippingStatus === CONSTANTS.SHIPPING_STATUS.PREPARING && 
                                         !row.shippingNumber && (
                                            <>
                                                <Button 
                                                    onClick={() => handleAddressModify(row)} 
                                                    variant="contained" 
                                                    color="secondary" 
                                                    size="small"
                                                    disabled={isPageBusy}
                                                >
                                                    수정
                                                </Button>
                                                <br/>
                                            </>
                                        )}
                                        ({row.order.payment.buyerZipcode})<br/>
                                        {row.order.payment.buyerAddress}
                                    </TableCell>
                                    <TableCell>{row.order.shippingMemo}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </ListTableContainer>
                
                {/* 송장번호 등록/수정 다이얼로그 */}
                <Dialog 
                    onClose={shippingSaving ? undefined : closeShippingDialog} 
                    open={dialogOpen} 
                    maxWidth="sm" 
                    fullWidth
                >
                    <DialogTitle>송장번호 등록 / 수정</DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2} mt={1}>
                            <TextField
                                label="배송상태"
                                select
                                name="shippingStatus"
                                value={dialogValues.shippingStatus}
                                onChange={handleDialogFieldChange}
                                size="small"
                                disabled={shippingSaving}
                            >
                                <MenuItem value={CONSTANTS.SHIPPING_STATUS.PREPARING}>상품준비중</MenuItem>
                                <MenuItem value={CONSTANTS.SHIPPING_STATUS.SHIPPING}>배송중</MenuItem>
                                <MenuItem value={CONSTANTS.SHIPPING_STATUS.COMPLETED}>배송완료</MenuItem>
                            </TextField>
                            <TextField
                                label="택배사"
                                select
                                name="shippingCompany"
                                value={dialogValues.shippingCompany}
                                onChange={handleDialogFieldChange}
                                size="small"
                                disabled={dialogValues.shippingStatus === CONSTANTS.SHIPPING_STATUS.PREPARING || shippingSaving}
                            >
                                <MenuItem value="우체국택배">우체국택배</MenuItem>
                                <MenuItem value="롯데택배">롯데택배</MenuItem>
                            </TextField>
                            <TextField
                                label="송장번호"
                                name="shippingNumber"
                                value={dialogValues.shippingNumber}
                                onChange={handleDialogFieldChange}
                                size="small"
                                inputRef={shippingNumberInputRef}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9-]*', maxLength: 35 }}
                                disabled={dialogValues.shippingStatus === CONSTANTS.SHIPPING_STATUS.PREPARING || shippingSaving}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={resetShippingNumber}
                            color="warning"
                            variant="outlined"
                            sx={FORM_DIALOG_WARNING_ACTION_BUTTON_SX}
                            disabled={shippingSaving}
                            startIcon={isShippingResetting ? BUTTON_LOADING_ICON : undefined}
                            aria-busy={isShippingResetting}
                        >
                            {isShippingResetting ? '처리 중...' : '초기화'}
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={closeShippingDialog} variant="outlined" color="secondary" sx={FORM_DIALOG_ACTION_BUTTON_SX} disabled={shippingSaving}>취소</Button>
                        <Button
                            onClick={updateShippingNumber}
                            variant="contained"
                            color="primary"
                            sx={FORM_DIALOG_ACTION_BUTTON_SX}
                            disabled={shippingSaving}
                            startIcon={isShippingUpdating ? BUTTON_LOADING_ICON : undefined}
                            aria-busy={isShippingUpdating}
                        >
                            {isShippingUpdating ? '저장 중...' : '저장'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* 페이지네이션 */}
                <Box sx={{ opacity: leaveGuardEnabled ? 0.6 : 1, pointerEvents: leaveGuardEnabled ? 'none' : 'auto' }}>
                    {totalCount > 0 && (
                        <Pagination curPage={curPage} offset={CONSTANTS.OFFSET} totalCount={totalCount} setCurPage={setCurPage} />
                    )}
                </Box>

                {/* Snackbar */}
                {snackbar.open && (
                    <Snackbars open={snackbar.open} severity={snackbar.severity} message={snackbar.message} handleClose={handleCloseSnackbar}/>
                )}
            </Paper>
        </Grid>
    )
}
